/**
 * Die Aufgaben-Bloecke der Uebersicht (V-01): "Heute" und "Ueberfaellig".
 * Nur abhaken und hinklicken (V-02).
 */
import { useLiveQuery } from 'dexie-react-hooks';
import { Link } from 'react-router-dom';
import { AlarmClock, FolderKanban, Star, Sun, Target } from 'lucide-react';

import { Card, Empty } from '@/modules/dashboard/Card';
import { AreaDot } from '@/modules/areas/AreaDot';
import { useAreaColor, useAreaMap } from '@/modules/areas/useAreas';
import { WochenzieleListe } from '@/modules/planning/components/Wochenfokus';
import { toggleTaskDone } from '@/db/repositories/tasks';
import { db } from '@/db/schema';
import { pfad } from '@/app/fokus';
import { formatDe } from '@/lib/datetime';
import { verpasst } from '@/domain/tasks/wiederholung';
import { cn } from '@/lib/utils/cn';
import type { ID, LocalDate, Task } from '@/domain/types';

/** So viele Aufgaben stehen direkt im Block, der Rest ist einen Klick entfernt. */
const MAX = 10;

export function HeuteBlock({ aufgaben, heute }: { aufgaben: Task[]; heute: LocalDate }) {
  return (
    <Card title="Heute" icon={Sun} to="/aufgaben" badge={aufgaben.length > 0 ? String(aufgaben.length) : undefined}>
      <div className="flex flex-col gap-3">
        {aufgaben.length === 0
          ? <Empty>Für heute ist keine Aufgabe offen.</Empty>
          : <AufgabenListe aufgaben={aufgaben} />}
        <div className="border-t border-line pt-2.5">
          <WochenzieleListe heute={heute} />
        </div>
      </div>
    </Card>
  );
}

export function UeberfaelligBlock({ aufgaben, heute }: { aufgaben: Task[]; heute: LocalDate }) {
  return (
    <Card title="Überfällig" icon={AlarmClock} to="/aufgaben" badge={aufgaben.length > 0 ? String(aufgaben.length) : undefined}>
      {aufgaben.length === 0
        ? <Empty>Nichts ist liegen geblieben.</Empty>
        : <AufgabenListe aufgaben={aufgaben} datum={(t) => seit(t, heute)} />}
    </Card>
  );
}

/** "fällig Mo 21.09." oder "geplant Mo 21.09." - was frueher war, zaehlt. */
function seit(t: Task, heute: LocalDate): string {
  // Wiederkehrend (F-24): einmal da, mit Zaehler - nicht dreimal gestapelt.
  const mal = verpasst(t, heute);
  if (mal > 1) return `${mal}× verpasst`;
  const faellig = t.dueDate && t.dueDate < heute ? t.dueDate : undefined;
  const geplant = t.plannedDate && t.plannedDate < heute ? t.plannedDate : undefined;
  if (faellig && (!geplant || faellig <= geplant)) return `fällig ${formatDe(faellig, 'EEEEEE dd.MM.')}`;
  return `geplant ${formatDe(geplant!, 'EEEEEE dd.MM.')}`;
}

function AufgabenListe({ aufgaben, datum }: { aufgaben: Task[]; datum?: (t: Task) => string }) {
  const areaColor = useAreaColor();
  const areaMap = useAreaMap();
  const titel = useTitel();
  return (
    <ul className="flex flex-col gap-1">
      {aufgaben.slice(0, MAX).map((t) => (
        <li key={t.id} className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={false}
            onChange={() => void toggleTaskDone(t.id)}
            aria-label={`${t.title} erledigt`}
            className="size-4 shrink-0 accent-[var(--color-accent)]"
          />
          <AreaDot color={areaColor(t.areaId)} size="sm" name={areaMap.get(t.areaId ?? '')?.name} />
          {t.isBigThree && <Star className="size-3.5 shrink-0 fill-current text-accent-text" aria-label="Eine der wichtigsten drei" />}
          <span className={cn('min-w-0 flex-1 truncate', t.isBigThree && 'font-medium')}>{t.title}</span>
          {/* Verknuepfung sichtbar und anklickbar (Leitlinie 5). */}
          {t.projectId ? (
            <VerweisIcon to={pfad.projekt(t.projectId)} label={`Projekt: ${titel.get(t.projectId) ?? ''}`} icon={FolderKanban} />
          ) : t.goalId ? (
            <VerweisIcon to={pfad.ziel(t.goalId)} label={`Ziel: ${titel.get(t.goalId) ?? ''}`} icon={Target} />
          ) : null}
          {datum && <span className="shrink-0 text-[11px] tabular-nums text-danger">{datum(t)}</span>}
        </li>
      ))}
      {aufgaben.length > MAX && (
        <li><Link to="/aufgaben" className="text-[12px] text-ink-faint hover:text-ink hover:underline">+{aufgaben.length - MAX} weitere</Link></li>
      )}
    </ul>
  );
}

function VerweisIcon({ to, label, icon: Icon }: { to: string; label: string; icon: typeof Target }) {
  return (
    <Link to={to} title={label} aria-label={label} className="grid size-6 shrink-0 place-items-center rounded text-ink-faint hover:bg-surface-raised hover:text-ink">
      <Icon className="size-3.5" />
    </Link>
  );
}

/** Titel von Projekten und Zielen - fuer die Verweise an den Aufgaben. */
function useTitel(): Map<ID, string> {
  return useLiveQuery(async () => {
    const [projects, goals] = await Promise.all([db.projects.toArray(), db.goals.toArray()]);
    return new Map<ID, string>([...projects.map((p) => [p.id, p.title] as const), ...goals.map((g) => [g.id, g.title] as const)]);
  }, [], new Map<ID, string>());
}
